import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Header } from './components/Header'
import { Sidebar } from './components/Sidebar'
import { ChatBubble } from './components/ChatBubble'
import { LoginCard } from './components/LoginCard'
import { ThemeProvider } from './contexts/ThemeContext'
import { ChatProvider } from './contexts/ChatContext'
import { UserRoleProvider } from './contexts/UserRoleContext'
import { PWAProvider } from './contexts/PWAContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { OperatorProvider, useOperator } from './contexts/OperatorContext'
import { PinnedCardsProvider } from './contexts/PinnedCardsContext'
import { EventDataProvider } from './contexts/EventDataContext'
import { InstallPrompt } from './components/PWA/InstallPrompt'
import { UpdatePrompt } from './components/PWA/UpdatePrompt'
import { ConnectionStatus } from './components/PWA/ConnectionStatus'
import { OfflineIndicator } from './components/PWA/OfflineIndicator'

import { HomePage } from './pages/HomePage'
import { EventsPage } from './pages/EventsPage'
import { EventDetailPage } from './pages/EventDetailPage'
import { ReportsPage, ReportDetailPage } from './pages/ReportsPage'
import { LandingPage, LandingDetailPage } from './pages/LandingPage'
import { SettingsPage } from './pages/SettingsPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { SmartImportPage } from './pages/SmartImport'
import { VerifySmsPage } from './pages/VerifySmsPage'
import { AnalyticsPage } from './pages/AnalyticsPage'
import { EventPostMortemPage } from './pages/analytics/EventPostMortemPage'
import { CustomerSegmentPage } from './pages/analytics/CustomerSegmentPage'
import { DailyLivePage } from './pages/analytics/DailyLivePage'
import { PreEventReadinessPage } from './pages/analytics/PreEventReadinessPage'
import { ComparePage } from './pages/analytics/ComparePage'
import { RideIntelligencePage } from './pages/analytics/RideIntelligencePage'
import { HelpPage, HelpTopicDetail } from './pages/help'
import { RankingsPage } from './pages/RankingsPage'
import { InsightsPage } from './pages/InsightsPage'
import { EventHistoryPage } from './pages/EventHistoryPage'
import { IntelligencePage } from './pages/IntelligencePage'
import { SeasonPage } from './pages/SeasonPage'
import { DeepAnalyticsPage } from './pages/DeepAnalyticsPage'
import { RevenueBreakdownPage } from './pages/RevenueBreakdownPage'
import { GuestGroupsPage } from './pages/GuestGroupsPage'
import { LayoutMapPage } from './pages/LayoutMapPage'

import { CompanyDashboard } from './pages/company/CompanyDashboard'
import { CompanyEvents } from './pages/company/CompanyEvents'
import { CompanyRankings } from './pages/company/CompanyRankings'
import { CompanyTrends } from './pages/company/CompanyTrends'

import { lazy, Suspense } from 'react'
const UsersAdminPage = lazy(() => import('./pages/admin/UsersAdminPage').then(m => ({ default: m.UsersAdminPage })))
const ChatMonitorPage = lazy(() => import('./pages/admin/ChatMonitorPage').then(m => ({ default: m.ChatMonitorPage })))
const UsageAnalyticsPage = lazy(() => import('./pages/admin/UsageAnalyticsPage').then(m => ({ default: m.UsageAnalyticsPage })))
const PlatformAgentPage = lazy(() => import('./pages/admin/PlatformAgentPage').then(m => ({ default: m.PlatformAgentPage })))
const AdminInsightsPage = lazy(() => import('./pages/admin/InsightsPage').then(m => ({ default: m.InsightsPage })))

import { PortalDashboard, TenantDetailPage, FeatureFlagsPage, PortalUsersPage, ProvisionPage } from './pages/portal'
import { PortalSidebar } from './components/PortalSidebar'

function AppShell() {
  // Sidebar collapse state — auto-collapses on `<lg` widths so the chrome
  // doesn't crowd narrow viewports. Header chevron toggles it on demand.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 1024
  })
  useEffect(() => {
    const onResize = () => {
      if (typeof window === 'undefined') return
      setSidebarCollapsed(window.innerWidth < 1024)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <div className="flex h-screen max-h-screen flex-col bg-surface-page dark:bg-slate-900">
      <Header
        onToggleSidebar={() => setSidebarCollapsed((c) => !c)}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
        />
        <main className="flex flex-1 min-w-0 min-h-0 overflow-hidden bg-butler-lobby dark:bg-slate-900">
          <div className="flex-1 min-w-0 overflow-y-auto">
            <Routes>
              <Route path="/" element={<Navigate to="/company" replace />} />
              {/* Company section — aggregate views across all events */}
              <Route path="/company" element={<CompanyDashboard />} />
              <Route path="/company/events" element={<CompanyEvents />} />
              <Route path="/company/rankings" element={<CompanyRankings />} />
              <Route path="/company/trends" element={<CompanyTrends />} />
              <Route path="/company/plans" element={<CompanyRankings />} />
              <Route path="/company/attractions" element={<CompanyRankings />} />
              {/* Events section — single-event scoped views */}
              <Route path="/home" element={<HomePage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:id" element={<EventDetailPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/:id" element={<ReportDetailPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              {/* V2.5-B template analytics pages — eventId required in path. */}
              <Route
                path="/analytics/template/event-post-mortem/:eventId"
                element={<EventPostMortemPage />}
              />
              <Route
                path="/analytics/template/customer-segment/:eventId"
                element={<CustomerSegmentPage />}
              />
              <Route
                path="/analytics/template/daily-live/:eventId"
                element={<DailyLivePage />}
              />
              <Route
                path="/analytics/template/pre-event-readiness/:eventId"
                element={<PreEventReadinessPage />}
              />
              {/* V3 MVP (OVERSEER 2026-04-21): side-by-side event comparison.
                  Blank route prompts picker; params-route is deep-linkable. */}
              <Route path="/analytics/compare" element={<ComparePage />} />
              <Route
                path="/analytics/compare/:eventAId/vs/:eventBId"
                element={<ComparePage />}
              />
              {/* Copilot analytics — ride intelligence (Sprint 1-2) */}
              <Route path="/ride-intelligence" element={<RideIntelligencePage />} />
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/landing/:eventId" element={<LandingDetailPage />} />
              <Route path="/import" element={<SmartImportPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* V3 new pages — Rankings, Insights, Intelligence, Event History */}
              <Route path="/season" element={<SeasonPage />} />
              <Route path="/rankings" element={<RankingsPage />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/deep-analytics" element={<DeepAnalyticsPage />} />
              <Route path="/revenue" element={<RevenueBreakdownPage />} />
              <Route path="/intelligence" element={<IntelligencePage />} />
              <Route path="/guest-groups" element={<GuestGroupsPage />} />
              <Route path="/map" element={<LayoutMapPage />} />
              <Route path="/history" element={<EventHistoryPage />} />
              <Route path="/history/:eventFamily" element={<EventHistoryPage />} />
              {/* V3 Page-02 — support site. /help index + /help/:topic
                  walkthrough. Optional step index is deep-link shareable
                  per 02-support-site.md §route. */}
              <Route path="/help" element={<HelpPage />} />
              <Route path="/help/:topic" element={<HelpTopicDetail />} />
              <Route path="/help/:topic/step/:stepIndex" element={<HelpTopicDetail />} />
              {/* Admin section — platform-level views (lazy-loaded) */}
              <Route path="/admin/users" element={<Suspense fallback={<div className="p-6 text-center text-ink-soft animate-pulse">Loading...</div>}><UsersAdminPage /></Suspense>} />
              <Route path="/admin/chat-monitor" element={<Suspense fallback={<div className="p-6 text-center text-ink-soft animate-pulse">Loading...</div>}><ChatMonitorPage /></Suspense>} />
              <Route path="/admin/usage" element={<Suspense fallback={<div className="p-6 text-center text-ink-soft animate-pulse">Loading...</div>}><UsageAnalyticsPage /></Suspense>} />
              <Route path="/admin/agent" element={<Suspense fallback={<div className="p-6 text-center text-ink-soft animate-pulse">Loading...</div>}><PlatformAgentPage /></Suspense>} />
              <Route path="/admin/insights" element={<Suspense fallback={<div className="p-6 text-center text-ink-soft animate-pulse">Loading...</div>}><AdminInsightsPage /></Suspense>} />
              {/* Legacy V2/V3 redirects */}
              <Route path="/dashboard" element={<Navigate to="/home" replace />} />
              <Route path="/chat" element={<Navigate to="/home" replace />} />
              <Route path="/extras" element={<Navigate to="/analytics" replace />} />
              <Route path="/compare" element={<Navigate to="/analytics/compare" replace />} />
              <Route path="/canvases" element={<Navigate to="/reports" replace />} />
              {/* /season is declared above as SeasonPage */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </main>
        <ChatBubble />
      </div>
    </div>
  )
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-butler-lobby dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="font-display text-butler-600 dark:text-butler-200 tracking-wide animate-pulse-slow">
          Loading…
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Fork 11 (2026-04-17): /login must redirect signed-in users to /home so a
// successful POST /auth/login flips immediately into the hub, and so an
// already-authed user typing /login into the address bar doesn't stall on
// a stale sign-in card. Exported so fork-11 regression test can exercise
// the redirect without mounting the full provider tree.
//
// Fork 14 (2026-04-17): an in-flight SMS MFA challenge must win over the
// login card so the user can finish verifying without re-POSTing
// credentials. pendingMfa is only set for the sms path — TOTP accounts
// keep falling through to LoginCard's graceful-block error (Fork 11).
export function LoginRoute() {
  const { isAuthenticated, isLoading, pendingMfa } = useAuth()
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-butler-lobby dark:bg-slate-900">
        <div className="font-display text-butler-600 dark:text-butler-200 tracking-wide animate-pulse-slow">
          Loading…
        </div>
      </div>
    )
  }
  if (isAuthenticated) return <Navigate to="/home" replace />
  if (pendingMfa) return <Navigate to="/auth/verify-sms" replace />
  return <LoginCard />
}

// Fork 14 (2026-04-17): the SMS verify page is a top-level sibling of
// /login — it must NOT mount inside AuthGuard (the user isn't
// authenticated yet) and it must NOT mount inside AppShell (the sidebar /
// chat chrome reads claims that don't exist until verifyMfa succeeds).
// Route guard: require pendingMfa, bounce authed users to /home (no point
// re-verifying once cookies are live), bounce unauthed-no-challenge users
// to /login.
export function VerifySmsRoute() {
  const { isAuthenticated, isLoading, pendingMfa } = useAuth()
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-butler-lobby dark:bg-slate-900">
        <div className="font-display text-butler-600 dark:text-butler-200 tracking-wide animate-pulse-slow">
          Loading…
        </div>
      </div>
    )
  }
  if (isAuthenticated) return <Navigate to="/home" replace />
  if (!pendingMfa) return <Navigate to="/login" replace />
  return <VerifySmsPage />
}

// Fork 11 (2026-04-17): ChatProvider is mounted ONLY inside the authed
// subtree. Mounting it globally caused unauthenticated /login visitors to
// open a WebSocket, get closed with 4001 missing-auth, then reconnect every
// 3 s forever — pure battery drain and log noise. Gating it behind
// AuthGuard means the socket is created exactly when a valid session
// exists and torn down on logout.
function PortalShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 1024
  })

  return (
    <div className="flex h-screen max-h-screen flex-col bg-surface-page dark:bg-slate-900">
      <header
        className="h-14 flex items-center gap-3 px-4 shrink-0"
        style={{ background: 'linear-gradient(135deg, #1a1f36 0%, #2d3561 55%, #5A78FF 100%)' }}
      >
        <button
          type="button"
          onClick={() => setSidebarCollapsed(c => !c)}
          aria-label="Toggle sidebar"
          className="lg:hidden w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:bg-white/15"
        >
          <span className="text-lg">☰</span>
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold text-sm">
            MM
          </div>
          <span className="text-white font-bold text-[15px] tracking-tight">
            Magic Metrics
          </span>
        </div>
      </header>
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <PortalSidebar collapsed={sidebarCollapsed} />
        <main className="flex flex-1 min-w-0 min-h-0 overflow-hidden bg-slate-50 dark:bg-slate-900">
          <div className="flex-1 min-w-0 overflow-y-auto">
            <Routes>
              <Route path="/portal" element={<PortalDashboard />} />
              <Route path="/portal/users" element={<PortalUsersPage />} />
              <Route path="/portal/provision" element={<ProvisionPage />} />
              <Route path="/portal/tenants/:tenantId" element={<TenantDetailPage />} />
              <Route path="/portal/tenants/:tenantId/features" element={<FeatureFlagsPage />} />
              <Route path="/" element={<Navigate to="/portal" replace />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  )
}

function AuthedApp() {
  const { features } = useOperator()
  const isPortal = features.portal_mode === true

  if (isPortal) {
    return (
      <AuthGuard>
        <PortalShell />
      </AuthGuard>
    )
  }

  return (
    <AuthGuard>
      <EventDataProvider>
        <PinnedCardsProvider>
          <ChatProvider>
            <AppShell />
            <InstallPrompt />
            <UpdatePrompt />
            <ConnectionStatus />
          </ChatProvider>
        </PinnedCardsProvider>
      </EventDataProvider>
    </AuthGuard>
  )
}

function App() {
  return (
    <OperatorProvider>
    <ThemeProvider>
      <PWAProvider>
        <AuthProvider>
          <UserRoleProvider>
            <div className="min-h-screen bg-background text-foreground">
              <OfflineIndicator />
              <Routes>
                <Route path="/login" element={<LoginRoute />} />
                <Route path="/auth/verify-sms" element={<VerifySmsRoute />} />
                <Route path="/*" element={<AuthedApp />} />
              </Routes>
            </div>
          </UserRoleProvider>
        </AuthProvider>
      </PWAProvider>
    </ThemeProvider>
    </OperatorProvider>
  )
}

export default App
